import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import { useMemo } from 'react'

import {
  calculateTotalDamagedArea,
  convertAreaCm2ToFt2,
  extractRooftopAnnotations,
  filterPolygonAnnotations,
  getAnnotationId,
} from './helpers'
import { useRooftopKestrelModels } from './useRooftopKestrelModels'

export type EnhancedRooftopThumbnail = {
  annotationId: string
  kestrelModelId: string
  section?: string
  area: number
  label?: string
  notes?: string
  severity?: string
  type?: string
  whatWeSee?: string
  whyItMatters?: string
  likelyCause?: string
  recommendedFix?: string
  damageTypes?: string[]
  rgb?: { entry_id: string; path: string }
  thermal?: { entry_id: string; path: string }
}

export type RooftopDamagesBySection = {
  [section: string]: EnhancedRooftopThumbnail[]
}

export type AllRooftopDamages = {
  totalDamagedAreaFt2: number
  damagesBySection: RooftopDamagesBySection
  allDamages: EnhancedRooftopThumbnail[]
}

export const useAllRooftopDamages = (
  unitId: string | undefined
): {
  allDamages: AllRooftopDamages | undefined
  isLoading: boolean
} => {
  const { kestrelModels, isLoading } = useRooftopKestrelModels(unitId, {
    enableFreshData: true,
  })

  const allDamages = useMemo<AllRooftopDamages | undefined>(() => {
    if (!kestrelModels || kestrelModels.length === 0) return undefined

    const allAnnotations = extractRooftopAnnotations(kestrelModels)
    const polygonAnns = filterPolygonAnnotations(allAnnotations)
    const totalDamagedAreaFt2 = calculateTotalDamagedArea(polygonAnns)

    const withAreaAndSection = polygonAnns
      .map(({ a, modelId }) => {
        const areaCm2 = a.area
        if (!areaCm2) return null

        const area = convertAreaCm2ToFt2(areaCm2)
        const id = getAnnotationId(a)

        const rgbPath = a.rooftopAnnotation?.rgbScreenshotPath
        const thermalPath = a.rooftopAnnotation?.thermalScreenshotPath
        const section = a.rooftopAnnotation?.section || 'Unknown Section'

        if (!rgbPath && !thermalPath) return null

        return {
          annotationId: id,
          kestrelModelId: modelId,
          section,
          area,
          label: a.label,
          notes: a.notes,
          severity: a.severity,
          type: a.type,
          whatWeSee: a.rooftopAnnotation?.whatWeSee,
          whyItMatters: a.rooftopAnnotation?.whyItMatters,
          likelyCause: a.rooftopAnnotation?.likelyCause,
          recommendedFix: a.rooftopAnnotation?.recommendedFix,
          damageTypes: a.rooftopAnnotation?.selected,
          rgb: rgbPath ? { entry_id: modelId, path: rgbPath } : undefined,
          thermal: thermalPath
            ? { entry_id: modelId, path: thermalPath }
            : undefined,
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)

    const sortedDamages = sortBy(withAreaAndSection, (d) => -d.area)
    const damagesBySection = groupBy(sortedDamages, 'section')

    return {
      totalDamagedAreaFt2,
      damagesBySection,
      allDamages: sortedDamages,
    }
  }, [kestrelModels])

  return { allDamages, isLoading }
}
